import { useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, X, Send } from 'lucide-react';
import { API } from '../App';
import { Dialog, DialogContent, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

/**
 * Public submission form for the Inspiration Gallery.
 * Visitors send in their own reuse projects → goes to the moderation queue.
 */
export default function SubmitInspirationDialog({ meta, onSubmitted, trigger }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [imageInput, setImageInput] = useState('');

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const toggleSet = (field, value) => {
    setForm((f) => {
      const set = new Set(f[field]);
      set.has(value) ? set.delete(value) : set.add(value);
      return { ...f, [field]: [...set] };
    });
  };

  const addImage = () => {
    const url = imageInput.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Image URLs must start with http:// or https://');
      return;
    }
    if (form.image_urls.length >= 10) {
      toast.warning('Max 10 images per submission.');
      return;
    }
    update('image_urls', [...form.image_urls, url]);
    setImageInput('');
  };

  const removeImage = (idx) => {
    update('image_urls', form.image_urls.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Project title is required');
      return;
    }
    if (form.image_urls.length === 0) {
      toast.error('Please add at least one image URL');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inspirations/submit`, form);
      toast.success('Thanks — your project has been submitted for review.');
      setForm(blankForm());
      setImageInput('');
      setOpen(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="submit-inspiration-trigger" className="bg-accent hover:bg-accent/90 text-accent-foreground">
            <Plus className="w-4 h-4 mr-2" /> Submit your project
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submit-inspiration-dialog">
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div>
            <h2 className="font-playfair text-2xl font-bold mb-1">Submit a reuse project</h2>
            <p className="text-xs text-muted-foreground">
              Tell us about a built project that uses reclaimed material. Submissions are reviewed
              before going live in the gallery.
            </p>
          </div>

          <Field label="Project title *">
            <Input
              required
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Aizkibel Library Extension"
              maxLength={120}
              data-testid="submit-title"
            />
          </Field>

          <Field label="Reuse story">
            <Textarea
              value={form.body}
              onChange={(e) => update('body', e.target.value)}
              rows={4}
              placeholder="What was reclaimed and how was it used? E.g. 'Made from railway sleepers from the town's former station.'"
              maxLength={2000}
              data-testid="submit-body"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Architect / firm">
              <Input
                value={form.architect}
                onChange={(e) => update('architect', e.target.value)}
                placeholder="Estudio Beldarrain"
                data-testid="submit-architect"
              />
            </Field>
            <Field label="Photographer">
              <Input
                value={form.photographer}
                onChange={(e) => update('photographer', e.target.value)}
                placeholder="Jon Cazenave"
                data-testid="submit-photographer"
              />
            </Field>
          </div>

          <Field label="Source URL (firm page or article)">
            <Input
              value={form.source_url}
              onChange={(e) => update('source_url', e.target.value)}
              placeholder="https://www.dezeen.com/..."
              type="url"
              data-testid="submit-source-url"
            />
          </Field>

          <Field label="Image URLs * (paste up to 10 — Squarespace/CDN/firm-site links are fine)">
            <div className="flex gap-2">
              <Input
                value={imageInput}
                onChange={(e) => setImageInput(e.target.value)}
                placeholder="https://..."
                type="url"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addImage();
                  }
                }}
                data-testid="submit-image-input"
              />
              <Button type="button" variant="outline" onClick={addImage} data-testid="submit-image-add">
                Add
              </Button>
            </div>
            {form.image_urls.length > 0 && (
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="submit-image-grid">
                {form.image_urls.map((url, idx) => (
                  <div key={url} className="relative group rounded border border-border/30 overflow-hidden">
                    <img src={url} alt="" className="w-full h-20 object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-black/70 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove image"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>

          {meta?.categories?.length > 0 && (
            <Field label="Categories (pick any that apply)">
              <div className="flex flex-wrap gap-1.5">
                {meta.categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleSet('categories', c)}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                      form.categories.includes(c)
                        ? 'bg-accent/20 text-accent border border-accent/40'
                        : 'border border-border/30 text-muted-foreground hover:text-accent'
                    }`}
                    data-testid={`submit-cat-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Field>
          )}

          {meta?.tags?.length > 0 && (
            <Field label="Tags">
              <div className="flex flex-wrap gap-1.5">
                {meta.tags.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleSet('tags', t)}
                    className={`text-[11px] px-2 py-0.5 rounded-full transition-colors ${
                      form.tags.includes(t)
                        ? 'bg-accent/20 text-accent border border-accent/40'
                        : 'border border-border/30 text-muted-foreground hover:text-accent'
                    }`}
                    data-testid={`submit-tag-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </Field>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-border/30">
            <Field label="Your name (optional)">
              <Input
                value={form.submitter_name}
                onChange={(e) => update('submitter_name', e.target.value)}
                data-testid="submit-name"
              />
            </Field>
            <Field label="Your email (optional)">
              <Input
                value={form.submitter_email}
                onChange={(e) => update('submitter_email', e.target.value)}
                type="email"
                data-testid="submit-email"
              />
            </Field>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="submit-inspiration-submit">
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit for review
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function blankForm() {
  return {
    title: '',
    body: '',
    architect: '',
    photographer: '',
    source_url: '',
    image_urls: [],
    categories: [],
    tags: [],
    submitter_name: '',
    submitter_email: '',
  };
}
